import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useScrollSpy } from "@/hooks/useScrollSpy";

type NavItem = {
  title: string;
  href: string;
  isExternal?: boolean;
};

type NavSection = {
  title: string;
  items: NavItem[];
};

const mainNavItems: (NavItem | NavSection)[] = [
  { title: "Home", href: "/" },
  { title: "Services", href: "/#services" },
  { title: "Pricing", href: "/#pricing" },
  { title: "AI Bootcamp", href: "/ai-bootcamp" },
  { title: "Resources", href: "/resources" },
  { title: "Downloads", href: "/downloads" },
  { title: "Blog", href: "/blog" }
];

const Navbar = () => {
  const location = useLocation();
  const activeSection = useScrollSpy();

  // Check if the current route matches this nav item
  const isActive = (href: string) => {
    // Special handling for the Blog link
    if (href === "/blog") {
      return location.pathname.startsWith("/blog");
    }

    // For the home page
    if (href === "/") {
      return location.pathname === "/" && !activeSection;
    }
    
    // For hash-based navigation (sections on the same page)
    if (href.startsWith("/#")) {
      const expectedSection = href.substring(1);
      // Ensure activeSection is truthy AND matches before marking active
      return location.pathname === "/" && activeSection && activeSection === expectedSection;
    }
    
    // For regular routes
    if (href.includes("#")) {
      const [baseRoute, hash] = href.split("#");
      return location.pathname === baseRoute && activeSection === `#${hash}`;
    }
    
    // For routes without hash
    return location.pathname === href;
  };

  return (
    <nav 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        "bg-white/80 backdrop-blur-md border-b shadow-sm py-3"
      )}
    >
      <div className="container max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center space-x-2 z-50"
          >
            <img 
              src="/lovable-uploads/003495b0-901f-40d9-b411-441059221674.png" 
              alt="Niumora Logo" 
              width={120}
              height={40}
              className="h-10 w-auto" 
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <NavigationMenu>
              <NavigationMenuList>
                {mainNavItems.map((item, index) => {
                  if ('href' in item) {
                    return (
                      <NavigationMenuItem key={index}>
                        <NavigationMenuLink
                          asChild
                          className={cn(
                            navigationMenuTriggerStyle(),
                            "data-[active=true]:bg-primary/10 data-[active=true]:text-primary"
                          )}
                          data-active={isActive(item.href)}
                        >
                          <Link to={item.href}>
                            {item.title}
                          </Link>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                    );
                  }
                  
                  return (
                    <NavigationMenuItem key={index}>
                      <NavigationMenuTrigger>{item.title}</NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-[200px] gap-2 p-4">
                          {item.items.map((subItem, subIndex) => (
                            <li key={subIndex}>
                              <Link to={subItem.href}>
                                <NavigationMenuLink
                                  asChild
                                  className={cn(
                                    "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                                    "data-[active=true]:bg-primary/10 data-[active=true]:text-primary"
                                  )}
                                  data-active={isActive(subItem.href)}
                                >
                                  {subItem.title}
                                </NavigationMenuLink>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <Link to="/#pricing">
              <Button variant="outline" size="sm" className="rounded-full">
                Pricing
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => window.open('https://cal.com/niumora-ai/project-consultation', '_blank')}
            >
              Book Free Call
            </Button>
          </div>

          {/* Mobile menu using Sheet component */}
          <Sheet>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <div className="flex justify-start mb-8">
                <img 
                  src="/lovable-uploads/003495b0-901f-40d9-b411-441059221674.png" 
                  alt="Niumora Logo" 
                  className="h-10" 
                />
              </div>
              <nav className="flex flex-col gap-4">
                {mainNavItems.map((item, index) => {
                  if ('href' in item) {
                    return (
                      <Link
                        key={index}
                        to={item.href}
                        className={cn(
                          "text-base font-medium px-2 py-2 hover:bg-accent rounded-md transition-colors",
                          isActive(item.href) ? "text-primary font-semibold" : "text-foreground/80"
                        )}
                      >
                        {item.title}
                      </Link>
                    );
                  }
                  
                  return (
                    <div key={index} className="space-y-3">
                      <h4 className="font-medium text-sm px-2">{item.title}</h4>
                      <div className="pl-4 space-y-2">
                        {item.items.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.href}
                            className={cn(
                              "block text-sm px-2 py-2 hover:bg-accent rounded-md transition-colors",
                              isActive(subItem.href) ? "text-primary font-semibold" : "text-foreground/80"
                            )}
                          >
                            {subItem.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })}
                
                <div className="mt-6 flex flex-col gap-3">
                  <Link to="/#pricing">
                    <Button variant="outline" size="sm" className="w-full rounded-full">
                      Pricing
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full rounded-full"
                    onClick={() => window.open('https://cal.com/niumora-ai/project-consultation', '_blank')}
                  >
                    Book Free Call
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
